import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useUser } from "./UserContext";
import { supabase } from "../utils/supabaseClient";

// All known permission keys — used only as a reference for admin full-access
const ALL_PERMISSION_KEYS = [
  "tender.create",
  "tender.view",
  "tender.edit",
  "tender.delete",
  "tender.send_proposal",
  "offer.create",
  "offer.edit",
  "offer.delete",
  "client.create",
  "client.edit",
  "client.delete",
  "template.view",
  "template.edit_layout",
  "template.edit_content",
  "template.create",
  "template.delete",
  "settings.view",
  "settings.permissions",
  "bill_validation.view",
] as const;

type PermissionsContextType = {
  /** Check if the current user has a specific permission */
  can: (permission: string) => boolean;
  /** Whether the current user is an org admin */
  isAdmin: boolean;
  /** The user's role in the current organization (free-text from DB) */
  role: string | null;
  /** Whether permissions are still loading */
  isLoading: boolean;
  /** Current org ID */
  orgId: string | null;
  /** Force refresh permissions from the server */
  refreshPermissions: () => Promise<void>;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = role === "admin";

  const loadPermissions = useCallback(async () => {
    if (!user?.id) {
      setPermissions(new Set());
      setRole(null);
      setOrgId(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Get the user's org membership
      const { data: membership, error: memberError } = await supabase
        .from("base_organisation_members")
        .select("organisation_id, role")
        .eq("user_id", user.id)
        .limit(1)
        .single();

      if (memberError || !membership) {
        setPermissions(new Set());
        setRole(null);
        setOrgId(null);
        return;
      }

      const userOrgId = membership.organisation_id;
      const userRole = membership.role as string;

      setOrgId(userOrgId);
      setRole(userRole);

      // Admin always has all permissions — no DB lookup needed
      if (userRole === "admin") {
        setPermissions(new Set(ALL_PERMISSION_KEYS));
        return;
      }

      // For any non-admin role, fetch configured permissions from DB
      const { data: permRows, error: permError } = await supabase
        .from("org_role_permissions")
        .select("permission_key")
        .eq("org_id", userOrgId)
        .eq("role", userRole)
        .eq("granted", true);

      if (permError) {
        console.error("Error loading permissions:", permError);
        setPermissions(new Set());
        return;
      }

      setPermissions(new Set((permRows || []).map((r) => r.permission_key)));
    } catch (error) {
      console.error("Error loading permissions:", error);
      setPermissions(new Set());
      setRole(null);
      setOrgId(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Load permissions on user change
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Subscribe to real-time permission changes
  useEffect(() => {
    if (!orgId) return;

    const channel = supabase
      .channel("org_role_permissions_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "org_role_permissions",
          filter: `org_id=eq.${orgId}`,
        },
        () => {
          loadPermissions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, loadPermissions]);

  const can = useCallback(
    (permission: string): boolean => {
      if (isAdmin) return true;
      return permissions.has(permission);
    },
    [permissions, isAdmin]
  );

  const value: PermissionsContextType = {
    can,
    isAdmin,
    role,
    isLoading,
    orgId,
    refreshPermissions: loadPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook to access the current user's permissions.
 *
 * Usage:
 *   const { can, isAdmin, role } = usePermissions();
 *   can('tender.delete') // → boolean
 */
export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);

  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }

  return context;
};
